import { Dispatch, MutableRefObject, SetStateAction, useCallback } from 'react';
import { Examiner, ExamTaskGroupCoordinator, ITaskGroup } from '../../../../models/taskGroup.types';
import { ActionMenuItem } from '../../../../components/ActionsMenu/ActionsMenu.types';
import { ExaminerType, MessageModal } from '../../ExaminerTreemap.types';
import { ExaminerFunction } from '../../../Assessment/Assessment.types';
import useSessionUser from '../../../../hooks/useSessionUser';
import { SubText } from '../../../../components/Prompt/Prompt.component';
import { addExaminingTeamChairman } from '../../../../services/exams/examiningTeam/examiningTeam.service';
import { PromptDetails } from '../../../../models/promptsTypeModels.types';
import ActionsMenu from '../../../../components/ActionsMenu/ActionsMenu.component';

export type UseExamTaskGroupCoordinatorActionsMenuProps = {
  setAction: Dispatch<SetStateAction<string>>;
  setOpenUnassignDialog: React.Dispatch<React.SetStateAction<boolean>>;
  openAssignModal: (title: string, type: ExaminerType, isMultiChoice?: boolean, assessmentId?: string) => void;
  removedFunctionKgzForGroupGroupNameRef: MutableRefObject<string>;
  examinerFunction: ExaminerFunction;
  data: ITaskGroup[];
  handleReload: () => void;
  setPromptSubTexts: React.Dispatch<React.SetStateAction<SubText[] | undefined>>;
  setPromptMainText: React.Dispatch<React.SetStateAction<string>>;
  selectedItemId: React.MutableRefObject<string>;
  selectedTaskGroup: React.MutableRefObject<ITaskGroup | undefined>;
  setIsErrorPromptVisible: React.Dispatch<React.SetStateAction<boolean>>;
  closeMenu: () => void;
  hasPermissionForGroupTaskActions: boolean;
  onModalFunctionAction: (data: PromptDetails) => void;
  setMessageModal: React.Dispatch<React.SetStateAction<MessageModal>>;
  removedFunctionKgzForGroupIdRef: MutableRefObject<string>;
  setEditExaminerNumberModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isTechAdmin: boolean;
};

export const useExamTaskGroupCoordinatorActionsMenu = ({
  examinerFunction,
  openAssignModal,
  removedFunctionKgzForGroupGroupNameRef,
  setAction,
  data,
  setOpenUnassignDialog,
  handleReload,
  setPromptMainText,
  setPromptSubTexts,
  selectedItemId,
  selectedTaskGroup,
  setIsErrorPromptVisible,
  closeMenu,
  hasPermissionForGroupTaskActions,
  onModalFunctionAction,
  setMessageModal,
  removedFunctionKgzForGroupIdRef,
  setEditExaminerNumberModalOpen,
  isTechAdmin
}: UseExamTaskGroupCoordinatorActionsMenuProps) => {
  const { user } = useSessionUser();

  const canEditKGZNumber = useCallback(
    (examiner: Examiner) => {
      return !(examinerFunction === ExaminerFunction.KGZ && examiner.examinerId === user.profile.sub);
    },
    [examinerFunction, user?.profile.sub],
  );

  const addExaminingTeamChairmanFunction = useCallback(() => {
    const taskGroup = data.find(
      (taskGroup) =>
        taskGroup.examTaskGroupCoordinators.findIndex(
          (examTaskGroupCoordinator) => examTaskGroupCoordinator.taskGroupCoordinatorId === selectedItemId.current,
        ) != -1,
    );
    if (taskGroup) {
      selectedTaskGroup.current = taskGroup;
      addExaminingTeamChairman(selectedItemId.current).then(
        () => {
          handleReload();
        },
        () => {
          setPromptMainText('Dodanie funkcji PZE dla:');
          setPromptSubTexts([
            { text: taskGroup.name },
            {
              text: 'nie powiodło się, spróbuj ponownie później.',
              isBold: true,
            },
          ]);
          setIsErrorPromptVisible(true);
        },
      );
    }
  }, [handleReload, data]);

  const getExamTaskGroupCoordinatorActionsMenu = useCallback(
    (examTaskGroupCoordinator: ExamTaskGroupCoordinator, assessmentId: string, rowIndex: number): JSX.Element => {
      const menuItems: ActionMenuItem[] = [];

      if (examTaskGroupCoordinator?.examiner?.examinerId) {
        menuItems.push({
          text: 'Usuń egzaminatorowi funkcję KGZ',
          disabled: isTechAdmin,
          onClick: () => {
            setAction('Usuń egzaminatorowi funkcję KGZ');
            setOpenUnassignDialog(true);
          },
        });
      }
      if (!examTaskGroupCoordinator?.examiner?.examinerId) {
        menuItems.push({
          text: 'Przypisz egzaminatorowi funkcję KGZ',
          disabled: isTechAdmin,
          onClick: () => {
            setAction('Przypisz egzaminatorowi funkcję KGZ');
            openAssignModal(
              `Przypisz funkcję Koordynatora Grupy Zadań (${removedFunctionKgzForGroupGroupNameRef.current})`,
              'KGZ',
              false,
              assessmentId,
            );
          },
        });
      }
      menuItems.push({
        text: 'Dodaj funkcję PZE',
        onClick: () => {
          setAction('Dodaj funkcję PZE');
          addExaminingTeamChairmanFunction();
        },
      });
      if (hasPermissionForGroupTaskActions && examTaskGroupCoordinator?.examiner) {
        menuItems.push({
          text: 'Wyślij wiadomość',
          onClick: () => {
            setAction('Wyślij wiadomość');
            setMessageModal({ open: true, type: 'standard' });
          },
        });
      }
      if (!examTaskGroupCoordinator?.examiner?.examinerId) {
        menuItems.push({
          text: 'Usuń funkcję KGZ',
          disabled: isTechAdmin,
          onClick: () => {
            setAction('Usuń funkcję KGZ');
            onModalFunctionAction({
              fullName: '',
              examinatorId: '',
              type: 'confirmation',
              otherText: removedFunctionKgzForGroupGroupNameRef.current,
              groupId: removedFunctionKgzForGroupIdRef.current,
              typePromp: 'KGZ',
            });
            closeMenu();
          },
        });
      }
      if (examTaskGroupCoordinator?.examiner?.examinerId && canEditKGZNumber(examTaskGroupCoordinator?.examiner)) {
        menuItems.push({
          text: 'Edytuj numer egzaminatora',
          onClick: () => {
            setAction('Edytuj numer egzaminatora');
            setEditExaminerNumberModalOpen(true);
          },
        });
      }
      return <ActionsMenu menuItems={menuItems} rowIndex={rowIndex} />;
    },
    [
      addExaminingTeamChairmanFunction,
      hasPermissionForGroupTaskActions,
      onModalFunctionAction,
      openAssignModal,
      canEditKGZNumber,
    ],
  );

  return {
    getExamTaskGroupCoordinatorActionsMenu,
  };
};
